import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/adminService';

function WaitlistManager() {
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approving, setApproving] = useState(null);
  const [filter, setFilter] = useState('pending');
  const { signOut } = useAuth();

  useEffect(() => {
    loadWaitlist();
  }, []);

  const loadWaitlist = async () => {
    try {
      setLoading(true);
      const data = await adminService.getWaitlist();
      setWaitlist(data.waitlist || []);
    } catch (err) {
      setError(err.message || 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (email) => {
    if (!window.confirm(`Approve ${email}? This will send them login credentials.`)) {
      return;
    }

    try {
      setApproving(email);
      await adminService.approveWaitlistUser(email);
      
      // Refresh the list
      await loadWaitlist();
      
      alert(`${email} has been approved and will receive login instructions.`);
    } catch (err) {
      alert(`Failed to approve ${email}: ${err.message}`);
    } finally {
      setApproving(null);
    }
  };

  const getPriorityColor = (volume) => {
    const colors = {
      '5000+': { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
      '1000-5000': { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' },
      '500-1000': { bg: '#fefce8', text: '#ca8a04', border: '#fde047' },
      '100-500': { bg: '#eff6ff', text: '#055efd', border: '#bfdbfe' },
      '0-100': { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' }
    };
    return colors[volume] || colors['0-100'];
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const getCallVolumeNumber = (volume) => {
    const ranges = {
      '5000+': 5000,
      '1000-5000': 1000,
      '500-1000': 500,
      '100-500': 100,
      '0-100': 0
    };
    return ranges[volume] || 0;
  };

  const filteredAndSortedWaitlist = waitlist
    .filter(user => filter === 'all' || user.status === filter)
    .sort((a, b) => {
      const aVolume = getCallVolumeNumber(a.monthly_call_volume);
      const bVolume = getCallVolumeNumber(b.monthly_call_volume);
      if (aVolume !== bVolume) return bVolume - aVolume;
      return (a.created_at || 0) - (b.created_at || 0);
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading waitlist...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f9fb' }}>
      <nav className="bg-white shadow-sm" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link 
                to="/dashboard" 
                className="font-inter text-sm font-medium px-3 py-2 rounded-md transition-colors"
                style={{ color: '#055efd' }}
                onMouseEnter={(e) => e.target.style.color = '#0449d4'}
                onMouseLeave={(e) => e.target.style.color = '#055efd'}
              >
                ← Dashboard
              </Link>
              <h1 className="text-xl font-montserrat font-semibold" style={{ color: '#222324' }}>
                Waitlist Management
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={loadWaitlist}
                className="font-inter text-sm font-medium px-3 py-2 rounded-md transition-colors"
                style={{ color: '#979ba4' }}
                onMouseEnter={(e) => e.target.style.color = '#222324'}
                onMouseLeave={(e) => e.target.style.color = '#979ba4'}
              >
                Refresh
              </button>
              <button
                onClick={signOut}
                className="font-inter text-sm font-medium px-3 py-2 rounded-md transition-colors"
                style={{ color: '#979ba4' }}
                onMouseEnter={(e) => e.target.style.color = '#222324'}
                onMouseLeave={(e) => e.target.style.color = '#979ba4'}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-6" style={{ borderRadius: '8px' }}>
              <div className="font-inter text-sm">{error}</div>
            </div>
          )}

          <div className="bg-white shadow-sm overflow-hidden" style={{ borderRadius: '16px', border: '1px solid #e5e7eb' }}>
            <div className="px-6 py-5">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-montserrat font-semibold" style={{ color: '#222324' }}>
                    Waitlist Users ({filteredAndSortedWaitlist.length})
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm font-inter" style={{ color: '#979ba4' }}>
                    Sorted by call volume (high to low), then by date (oldest first)
                  </p>
                </div>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="font-inter text-sm px-3 py-2 border rounded-md"
                  style={{ borderColor: '#e5e7eb', color: '#222324' }}
                >
                  <option value="pending">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="all">All Users</option>
                </select>
              </div>
            </div>
            
            {filteredAndSortedWaitlist.length === 0 ? (
              <div className="text-center py-12">
                <p className="font-inter text-sm" style={{ color: '#979ba4' }}>
                  {filter === 'pending' ? 'No pending users' : filter === 'approved' ? 'No approved users' : 'No users in waitlist'}
                </p>
              </div>
            ) : (
              <ul style={{ borderTop: '1px solid #e5e7eb' }}>
                {filteredAndSortedWaitlist.map((user, index) => {
                  const priorityColor = getPriorityColor(user.monthly_call_volume);
                  return (
                    <li key={user.email} className="px-6 py-4" style={{ borderBottom: index < filteredAndSortedWaitlist.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <span 
                                className="inline-flex items-center px-2.5 py-0.5 text-xs font-inter font-medium"
                                style={{ 
                                  backgroundColor: '#eff6ff',
                                  color: '#055efd',
                                  borderRadius: '12px'
                                }}
                              >
                                #{index + 1}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-inter font-medium truncate" style={{ color: '#222324' }}>
                                {user.email}
                              </p>
                              <p className="text-sm font-inter truncate" style={{ color: '#979ba4' }}>
                                {user.business_name} • {user.business_type}
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center space-x-4 text-sm">
                            <span 
                              className="inline-flex items-center px-2.5 py-0.5 text-xs font-inter font-medium"
                              style={{ 
                                backgroundColor: priorityColor.bg,
                                color: priorityColor.text,
                                borderRadius: '12px',
                                border: `1px solid ${priorityColor.border}`
                              }}
                            >
                              {user.monthly_call_volume} calls/month
                            </span>
                            <span className="font-inter" style={{ color: '#979ba4' }}>Created: {formatDate(user.created_at)}</span>
                            {user.status === 'approved' && (
                              <span className="font-inter font-medium" style={{ color: '#10b981' }}>
                                ✓ Approved {formatDate(user.approved_at)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {user.status === 'pending' ? (
                            <button
                              onClick={() => handleApprove(user.email)}
                              disabled={approving === user.email}
                              className="btn-success disabled:opacity-50"
                            >
                              {approving === user.email ? 'Approving...' : 'Approve'}
                            </button>
                          ) : (
                            <span className="inline-flex items-center px-3 py-2 text-sm font-inter font-medium" style={{ color: '#10b981' }}>
                              ✓ Approved
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WaitlistManager;